import { useState, useEffect } from 'react';

interface PositionCoords {
  start: { x: number; y: number };
  end: { x: number; y: number };
}

interface PositionOffset {
  x: number;
  y: number;
}

interface UsePositionCalculationProps {
  width?: number;
  height?: number;
  endX?: number;
  endY?: number;
  elementRef: React.RefObject<HTMLDivElement>;
  svgRef: React.RefObject<SVGSVGElement>;
  cornerNearStart?: boolean;
  halfPath?: boolean;
  isSmallScreen?: boolean;
}

const MARGIN = 10;

const calculateOffset = (
  coords: PositionCoords,
  svg: SVGSVGElement,
  height: number,
  cornerNearStart: boolean
): PositionOffset => {
  const horizontalAdjustment = cornerNearStart ? 0 : height / 2;
  const x =
    coords.end.x <= 0
      ? -svg.getBoundingClientRect().width + coords.start.x + MARGIN
      : coords.start.x - MARGIN;

  const y =
    coords.end.y < 0
      ? -svg.getBoundingClientRect().height + coords.start.y + MARGIN - horizontalAdjustment
      : coords.start.y - MARGIN + horizontalAdjustment;

  return { x: x, y: y };
};

const calculateStartAndEnd = (
  endX: number,
  endY: number,
  element: HTMLDivElement,
  cornerNearStart: boolean,
  halfPath: boolean,
  isSmallScreen: boolean
): PositionCoords => {
  const elementRect = element.getBoundingClientRect();
  const startX = elementRect?.width / 2;
  const startY = elementRect?.height / 2;

  const relativeEndX = endX - element.offsetLeft;
  const relativeEndY = endY - element.offsetTop;

  const verticalOffset = cornerNearStart ? 0 : elementRect?.height / 2;
  const horizontalAdjustment = cornerNearStart ? elementRect?.width / 2 : 0;
  const startXOffset = relativeEndX > 0 ? horizontalAdjustment : -horizontalAdjustment;

  const baseCoords = {
    start: {
      x: startX + startXOffset,
      y: startY,
    },
    end: {
      x: relativeEndX,
      y: relativeEndY + (relativeEndY < 0 ? verticalOffset : -verticalOffset),
    },
  };

  if (halfPath) {
    return {
      start: baseCoords.start,
      end: isSmallScreen
        ? { x: baseCoords.end.x, y: baseCoords.start.y }
        : { x: baseCoords.start.x, y: baseCoords.end.y },
    };
  }

  return baseCoords;
};

export const useFlowPosition = ({
  endX = 0,
  endY = 0,
  elementRef,
  svgRef,
  cornerNearStart = false,
  halfPath = false,
  isSmallScreen = false,
}: UsePositionCalculationProps) => {
  const [startEndCoords, setStartEndCoords] = useState<PositionCoords>({
    start: { x: 0, y: 0 },
    end: { x: 0, y: 0 },
  });
  const [offset, setOffset] = useState<PositionOffset>({ x: 0, y: 0 });

  useEffect(() => {
    if (elementRef.current && svgRef.current && endX && endY) {
      setTimeout(() => {
        const coords = calculateStartAndEnd(
          endX,
          endY,
          elementRef.current as HTMLDivElement,
          cornerNearStart,
          halfPath,
          isSmallScreen
        );
        setStartEndCoords(coords);

        const calculatedOffset = calculateOffset(
          coords,
          svgRef.current as SVGSVGElement,
          elementRef.current?.offsetHeight || 0,
          cornerNearStart
        );
        setOffset(calculatedOffset);
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endX, endY, elementRef, svgRef, halfPath, isSmallScreen]);

  return { startEndCoords, offset };
};
